import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import { useDarkMode } from '../DarkModeContext.js';
import './Cart.css';
import CancelIcon from '@mui/icons-material/Cancel';
import box from '../assets/empty-box.png';
import np from '../assets/no.jpg';
import CryptoJS from 'crypto-js';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import RFQModal from './RFQModal.js';

const Cart = () => {
  const [cartItems, setCartItems] = useState([]);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const { darkMode } = useDarkMode();

  // --- Form State ---
  const [shippingDetails, setShippingDetails] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    zip: ''
  });

  // We capture card details in state for the UI, but we NEVER send them to the API.
  const [cardDetails, setCardDetails] = useState({
    cardName: '',
    cardNumber: '',
    expiry: '',
    cvv: ''
  });

  useEffect(() => {
    const savedCart = JSON.parse(localStorage.getItem('cart')) || [];
    setCartItems(savedCart);
  }, []);

  const updateQuantity = (id, newQuantity) => {
    if (newQuantity < 1) return;
    const updatedCart = cartItems.map(item =>
      item.id === id ? { ...item, quantity: newQuantity } : item
    );
    setCartItems(updatedCart);
    localStorage.setItem('cart', JSON.stringify(updatedCart));
  };

  const removeItem = (id) => {
    const updatedCart = cartItems.filter(item => item.id !== id);
    setCartItems(updatedCart);
    localStorage.setItem('cart', JSON.stringify(updatedCart));
    if (updatedCart.length === 0) setShowCheckout(false);
  };

  const printPage = () => {
    window.print();
  };

  const handleInputChange = (e, setter) => {
    const { name, value } = e.target;
    setter(prev => ({ ...prev, [name]: value }));
  };

  // --- THE MAGIC HAPPENS HERE ---
  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    setLoading(true);

    const browsingHistory = JSON.parse(localStorage.getItem('browsingHistory')) || [];

    // 1. Generate a mock secure token instead of sending the credit card
    const mockTransactionToken = `tok_secure_${Math.floor(Math.random() * 1000000)}`;

    // 2. Bundle the extra data (City, Zip, Payment Status) into the string your API already accepts: 'address'
    const bundledAddressInfo = `${shippingDetails.address}, ${shippingDetails.city}, ${shippingDetails.zip} | [PAYMENT STATUS: Pre-Authorized | TXN_ID: ${mockTransactionToken}]`;

    // 3. Format the payload EXACTLY how your Cloudflare Worker expects it
    const payload = {
      name: shippingDetails.fullName,
      email: shippingDetails.email,
      phone: shippingDetails.phone,
      address: bundledAddressInfo, 
      cart: cartItems.map(item => ({ id: item.id, quantity: item.quantity })),
      browsingHistory: browsingHistory.map(item => ({ id: item.id })),
    };

    try {
      const response = await fetch('https://api.trustglobal.com.bd/api/submit-rfq', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Something went wrong. Please try again.');
      }

      // Success! Show confirmation popup
      setShowConfirmation(true);

    } catch (err) {
      console.error(err);
      alert("Failed to submit order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
    setCartItems([]);
    localStorage.removeItem('cart');
    setShowCheckout(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className={`container mt-5 cart-container ${darkMode ? 'dark-mode' : ''}`}>
      <h1 className="text-center">Your Cart</h1>

      {/* Custom Popup Modal */}
      {/* Custom Popup Modal Using Portal */}
{showConfirmation && ReactDOM.createPortal(
  <div className="custom-modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <div className={`custom-modal-content text-center p-4 shadow-lg rounded ${darkMode ? 'bg-dark text-light' : 'bg-white text-dark'}`} style={{ zIndex: 10000, position: 'relative' }}>
      
      <h2 className="text-success mb-3">Order Request Received!</h2>
      <div className="alert alert-info border-0 rounded" style={{ backgroundColor: '#e2f0fb', color: '#055160' }}>
        <h5 className="alert-heading fw-bold">Next Steps</h5>
        <p className="mb-0">
          Our team will contact you shortly to <strong>confirm your shipping address</strong> and verify stock availability before shipping your order.
        </p>
      </div>
      <p className="text-muted small mt-3">
        Remember: No funds will be formally deducted until this confirmation is complete and your product is ready to ship.
      </p>
      <button className="btn btn-primary mt-3 px-5 py-2 fw-bold" onClick={handleCloseConfirmation}>
        Understood
      </button>

    </div>
  </div>,
  document.body // <-- This tells React to append the modal directly to the <body> tag
)}

      {cartItems.length === 0 ? (
        <div className="container-fluid mt-100">
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-body cart">
                  <div className="col-sm-12 empty-cart-cls text-center">
                    <img src={box} alt="EMPTY CART" width="130" height="130" className="img-fluid mb-4 mr-3" />
                    <h3><strong>Your Cart is Empty</strong></h3>
                    <h4>🛠️ Start adding items to build your perfect order!</h4>
                    <a href="/" className="btn btn-primary cart-btn-transform m-3" data-abc="true">continue shopping</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="row">
          <div className={showCheckout ? "col-lg-7" : "col-lg-12"}>
            <ul className="list-group">
              {cartItems.map(item => (
                <li key={item.id} className="list-group-item">
                  <div className="row align-items-center">
                    <div className="col-md-4 img-container">
                      <Link to={`/product/${item.id}`}  className="no-link">
                        <img src={item.imageUrls[0]} alt={item.name} className="img-fluid" onError={(e) => e.target.src = np} />
                      </Link>
                    </div>

                    <div className="col-md-4 item-details">
                      <h3>
                        <Link to={`/product/${item.id}`} className="no-link">
                          {item.name.length > 50 ? `${item.name.substring(0, 20)}...` : item.name}
                        </Link>
                      </h3>
                      <p>
                        <Link to={`/product/${item.id}`} className="no-link">
                          Item ID: {item.id} | Category: {item.category}
                        </Link>
                      </p>
                      <p>
                        <Link to={`/product/${item.id}`} className="no-link">
                          Price: BDT {item.price}
                        </Link>
                      </p>
                    </div>

                    <div className="col-auto">
                      <div className="quantity-control">
                        <button
                          className="btn btn-warning px-3 me-2 bold-button minus-button"
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        >
                          <RemoveIcon />
                        </button>
                        <input type="text" className="form-control quantity" value={item.quantity} readOnly />
                        <button
                          className="btn btn-success px-3 ms-2 bold-button plus-button"
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        >
                          <AddIcon />
                        </button>
                        <button className="btn btn-danger btn-remove" onClick={() => removeItem(item.id)}>
                          <CancelIcon />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="divider"></div>
                </li>
              ))}
            </ul>

            <div className="last-page-list mt-3">
              <RFQModal />
              <button onClick={printPage} className="btn btn-outline-primary mb-3 ms-2 btn-print">
                Print Cart
              </button>
              
              {!showCheckout && (
                <button 
                  onClick={() => setShowCheckout(true)} 
                  className="btn btn-success d-block mt-3 w-100 py-3 fw-bold"
                  style={{ fontSize: '1.2rem' }}
                >
                  Proceed to Secure Checkout
                </button>
              )}
            </div>
          </div>

          {/* Checkout Panel */}
          {showCheckout && (
            <div className="col-lg-5 checkout-panel">
              <div className="card shadow-sm border-0">
                <div className="card-body">
                  <h3 className="card-title mb-4">Secure Checkout</h3>
                  
                  <div className="alert alert-warning checkout-disclaimer" role="alert">
                    <h5 className="alert-heading text-dark fw-bold">Order Placement Policy</h5>
                    <p className="mb-2 text-dark small">By placing your order, you acknowledge the following terms:</p>
                    <ul className="mb-0 text-dark small">
                      <li><strong>Stock Verification:</strong> All orders are manually reviewed for stock availability. If an item is out of stock, your order will be cancelled and you will be notified.</li>
                      <li><strong>Delayed Billing:</strong> Placing this order will NOT cause an immediate removal of funds. A pre-authorization may be placed on your card, but funds will only be formally captured when your product is confirmed and ready for shipping.</li>
                    </ul>
                  </div>

                  <form onSubmit={handlePlaceOrder}>
                    <h5 className="mb-3 mt-4">Shipping & Contact Details</h5>
                    <div className="mb-3">
                      <label className="form-label">Full Name</label>
                      <input type="text" name="fullName" value={shippingDetails.fullName} onChange={(e) => handleInputChange(e, setShippingDetails)} className="form-control" placeholder="John Doe" required />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Email Address</label>
                      <input type="email" name="email" value={shippingDetails.email} onChange={(e) => handleInputChange(e, setShippingDetails)} className="form-control" placeholder="john@example.com" required />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Phone Number</label>
                      <input type="tel" name="phone" value={shippingDetails.phone} onChange={(e) => handleInputChange(e, setShippingDetails)} className="form-control" placeholder="+880 1..." required />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Shipping Address</label>
                      <textarea name="address" value={shippingDetails.address} onChange={(e) => handleInputChange(e, setShippingDetails)} className="form-control" rows="2" placeholder="Street, Apartment, suite, etc." required></textarea>
                    </div>
                    <div className="row mb-3">
                      <div className="col-6">
                        <label className="form-label">City</label>
                        <input type="text" name="city" value={shippingDetails.city} onChange={(e) => handleInputChange(e, setShippingDetails)} className="form-control" placeholder="City" required />
                      </div>
                      <div className="col-6">
                        <label className="form-label">Postal Code</label>
                        <input type="text" name="zip" value={shippingDetails.zip} onChange={(e) => handleInputChange(e, setShippingDetails)} className="form-control" placeholder="Zip/Postal" required />
                      </div>
                    </div>

                    <hr className="my-4" />

                    <h5 className="mb-3">Payment Information</h5>
                    <div className="mb-3">
                      <label className="form-label">Name on Card</label>
                      <input type="text" name="cardName" value={cardDetails.cardName} onChange={(e) => handleInputChange(e, setCardDetails)} className="form-control" placeholder="John Doe" required />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Card Number</label>
                      <input type="text" name="cardNumber" value={cardDetails.cardNumber} onChange={(e) => handleInputChange(e, setCardDetails)} className="form-control" placeholder="0000 0000 0000 0000" maxLength="19" required />
                    </div>
                    <div className="row mb-3">
                      <div className="col-6">
                        <label className="form-label">Expiry (MM/YY)</label>
                        <input type="text" name="expiry" value={cardDetails.expiry} onChange={(e) => handleInputChange(e, setCardDetails)} className="form-control" placeholder="MM/YY" maxLength="5" required />
                      </div>
                      <div className="col-6">
                        <label className="form-label">CVV</label>
                        <input type="text" name="cvv" value={cardDetails.cvv} onChange={(e) => handleInputChange(e, setCardDetails)} className="form-control" placeholder="123" maxLength="4" required />
                      </div>
                    </div>
                    
                    <button type="submit" className="btn btn-primary w-100 py-3 mt-3 fw-bold" style={{ fontSize: '1.1rem' }} disabled={loading}>
                      {loading ? 'Processing...' : 'Confirm & Place Order'}
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-light text-muted w-100 py-2 mt-2" 
                      onClick={() => setShowCheckout(false)}
                      disabled={loading}
                    >
                      Back to Cart
                    </button>
                  </form>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="contact-info mt-5 text-center">
        <p className="fw-bold mb-1">Contact Us</p>
        <p className="mb-1">Email: info@tglobal.store</p>
        <p className="mb-1">Phone: +1 561 925 7771 </p>
        <p>Facebook: <a href="https://www.facebook.com/realtrustglobal/">Facebook Page</a></p>
      </div>
    </div>
  );
};

export default Cart;